// Sesiune 82: modal global care intercepteaza descarcari PDF pentru guest.
// Asculta event-ul 'pdf-lead-gate' dispatchat de openPdfWithGuestGate().
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import PhoneInput from './common/PhoneInput';
import { pdfLoaderUrl } from '../utils/pdfGuestGate';

function appendQuery(url, key, value) {
  if (!value) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}${key}=${encodeURIComponent(value)}`;
}

const LeadCaptureGate = () => {
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null); // { url, sursa, id, mode }
  const [nume, setNume] = useState('');
  const [email, setEmail] = useState('');
  const [telefon, setTelefon] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    const handler = (ev) => {
      const d = ev.detail || {};
      if (!d.url || !d.sursa || !d.id) return;
      setPending(d);
      setNume(''); setEmail(''); setTelefon(''); setErr('');
      setOpen(true);
    };
    window.addEventListener('pdf-lead-gate', handler);
    return () => window.removeEventListener('pdf-lead-gate', handler);
  }, []);

  const canSubmit = nume.trim().length > 0 && (email.trim().length > 0 || telefon.trim().length > 0);

  const onSubmit = async () => {
    if (!pending || !canSubmit) return;
    setSubmitting(true); setErr('');
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const res = await fetch(`${apiBase}/api/pdf-lead/${pending.sursa}/${encodeURIComponent(pending.id)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nume: nume.trim(), email: email.trim(), telefon: telefon.trim(), lang: i18n.language || 'ro' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.lead_token) {
        setErr(data.detail === 'email_or_phone_required'
          ? t('lead_gate.err_email_or_phone', 'Introdu adresa de email SAU numar de telefon.')
          : data.detail === 'nume_required'
          ? t('lead_gate.err_nume', 'Numele este obligatoriu.')
          : t('lead_gate.err_generic', 'Eroare. Incearca din nou.'));
        setSubmitting(false);
        return;
      }
      // Sesiune 93 (Faza 1 R5): salveaza user_token JWT guest in sessionStorage pentru ca
      // pageview-urile ulterioare (trackPageview) sa includa Authorization Bearer cu
      // identitatea guest si sa fie atribuite corect in BI (vizite, country, etc.).
      if (data.user_token) {
        try { sessionStorage.setItem('oferte_token', data.user_token); } catch (_) { /* ignore */ }
      }
      const finalUrl = appendQuery(pending.url, 'lead_token', data.lead_token);
      const loaderUrl = pdfLoaderUrl(finalUrl);
      setOpen(false);
      setSubmitting(false);
      if (pending.mode === 'open') window.open(loaderUrl, '_blank');
      else window.location.assign(loaderUrl);
    } catch (_) {
      setErr(t('lead_gate.err_generic', 'Eroare. Incearca din nou.'));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) setOpen(v); }}>
      <DialogContent data-testid="lead-capture-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle data-testid="lead-capture-title">
            {t('lead_gate.title', 'Date contact pentru descarcare PDF')}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm font-semibold text-red-600" data-testid="lead-capture-message">
          {t('lead_gate.message', 'Daca doriti sa descarcati detaliile ofertei in format PDF, va rugam sa introduceti adresa mail SAU numar de telefon.')}
        </p>
        <div className="space-y-3">
          <div>
            <Label htmlFor="lead-nume">{t('lead_gate.nume', 'Nume')} *</Label>
            <Input id="lead-nume" value={nume} onChange={(e) => setNume(e.target.value)} data-testid="lead-input-nume" autoFocus />
          </div>
          <div>
            <Label htmlFor="lead-email">{t('lead_gate.email', 'Email')}</Label>
            <Input id="lead-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="lead-input-email" placeholder="nume@exemplu.com" />
          </div>
          <div>
            <Label>{t('lead_gate.telefon', 'Telefon')}</Label>
            <PhoneInput value={telefon} onChange={setTelefon} testid="lead-input-telefon" />
          </div>
          {err && <p className="text-xs text-red-600" data-testid="lead-capture-error">{err}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting} data-testid="lead-capture-cancel">
            {t('lead_gate.cancel', 'Renunta')}
          </Button>
          <Button onClick={onSubmit} disabled={!canSubmit || submitting} data-testid="lead-capture-submit" className="bg-red-600 hover:bg-red-700 text-white">
            {submitting ? t('lead_gate.submitting', 'Se trimite...') : t('lead_gate.submit', 'Trimite si descarca PDF')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LeadCaptureGate;
